import { useCombobox } from "@mantine/core";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { format } from "date-fns";
import React from "react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import Skeleton from "react-loading-skeleton";
import { useParams, useSearchParams } from "react-router-dom";
import { Tooltip } from "react-tooltip";

import { useSiteContext } from "~/contexts/SiteContext";
import { getGlobalSettings, setGlobalSettings } from "~/helpers/globalSettings";
import { urlWithHighlight } from "~/helpers/siteHelper";
import { useSitePermissions } from "~/hooks/permissions/useSitePermissions";
import humanizeString from "~/pages/GoogleDashboardPage/utils/humanizeString";
import { PagesResponseWithAuth } from "~/pages/dashboard/PagesPage";
import { IgnoreRecrawlPayload } from "~/pages/dashboard/PagesPage.tsx";

import { Ids, Page, PageBulkRecrawlAttributes } from "../../../api/Api";
import { Icon } from "../../../assets/icons/Icon";
import NoData from "../../containers/NoData";
import Modal from "../../ui/modal/Modal";
import { Pagination } from "../../ui/pagination/Pagination";
import { PaginationConfig } from "../../ui/pagination/Pagination";
import PageVisibilityPerformanceBars from "../../visibilityPerformance/PageVisibilityPerformanceBars";
import { isEngineHealthy } from "../../visibilityPerformance/engineHealth";
import ActionPopover from "./ActionPopover";
import MiddleTruncateUrl from "./MiddleTruncateUrl";
import PageFilters from "./PageFilters";
import styles from "./PageTable.module.scss";
import AddUrlModalContent from "./modals/AddUrlModalContent";
import BulkIgnoreModalContent from "./modals/BulkIgnoreModalContent";
import CrawlModalContent from "./modals/CrawlModalContent";
import {
  BulkIgnoreAction,
  IGNORE_COMPATIBLE_IGNORED_STATUSES,
  IGNORE_COMPATIBLE_PENDING_STATUSES,
  getBulkIgnoreActionFromStatuses,
} from "./statusConstants";

const DATE_FORMAT = "dd.MM.yyyy";

export interface PageListProps {
  pagination: PaginationConfig;
  setPagination: (pagination: PaginationConfig) => void;
  isLoading: boolean;
  isFetching?: boolean;
  setSelectedStatuses: (
    statuses: string[] | ((prev: string[]) => string[]),
  ) => void;
  selectedStatuses: string[];
  recrawlPage: (id: number) => void;
  ignoreRecrawlPage: (payload: IgnoreRecrawlPayload) => void;
  recrawlPagesBulk: (ids: PageBulkRecrawlAttributes) => void;
  data?: PagesResponseWithAuth;
  softCrawlPage: () => void;
  forceRecrawlPage: () => void;
  ignorePagesBulk: (ids: PageBulkRecrawlAttributes) => void;
  createPageUrl: (id: string) => void;
  urlServerError: string | null;
  setUrlServerError: (error: string) => void;
  searchValue?: string;
  setSearchValue?: (value: string) => void;
  sorting: string[];
  setSorting: (sorting: string[]) => void;
  sortable?: boolean;
  sortField?: string;
  selectedPageTypes: string[];
  setSelectedPageTypes: (
    types: string[] | ((prev: string[]) => string[]),
  ) => void;
  selectedCustomizations: string[];
  setSelectedCustomizations: (
    values: string[] | ((prev: string[]) => string[]),
  ) => void;
  pageTypesDetail: Record<string, number>;
  pageStatusesDetail: Record<string, number>;
  canGenerate?: boolean;
  onGenerateClick?: (selectedIds: number[]) => void;
  advancedConditions?: import("./AdvancedConditionsModal").ConditionGroup[];
  onAdvancedConditionsChange?: (
    groups: import("./AdvancedConditionsModal").ConditionGroup[],
  ) => void;
}

type ModalType = "crawl" | "bulkIgnore" | "addUrl" | null;

type SortDirection = "ASC" | "DESC" | null;

const getSortStateForColumn = (
  columnId: string,
  sorting: string[],
): SortDirection => {
  if (!sorting.length) return null;

  const sortEntry = sorting.find((entry) => entry.startsWith(columnId + " "));
  if (!sortEntry) return null;

  return sortEntry.endsWith("ASC") ? "ASC" : "DESC";
};

const MODAL_CONFIGS = {
  crawl: {
    title: (crawlType: "soft" | "force") =>
      crawlType === "soft" ? "Soft Crawl" : "Force Recrawl",
    buttonLabel: "Crawl",
    buttonColor: "primary",
    action: (
      closeModal: () => void,
      softCrawlPage: () => void,
      forceRecrawlPage: () => void,
      crawlType?: "soft" | "force",
    ) => {
      if (crawlType === "soft") {
        softCrawlPage();
      } else if (crawlType === "force") {
        forceRecrawlPage();
      }
      closeModal();
    },
  },
  bulkIgnore: {
    title: (action: BulkIgnoreAction) =>
      action === "include" ? "Include in crawling" : "Ignore for crawling",
    buttonLabel: (action: BulkIgnoreAction) =>
      action === "include" ? "Include" : "Ignore",
    buttonColor: "danger",
    action: (
      closeModal: () => void,
      ignorePagesBulk: (ids: PageBulkRecrawlAttributes) => void,
      selectedIdsToIgnore: number[],
      combobox: ReturnType<typeof useCombobox>,
      setIsOpened: (isOpen: boolean) => void,
      setSelectedIds: (ids: Ids) => void,
    ) => {
      ignorePagesBulk({ ids: selectedIdsToIgnore });
      combobox.resetSelectedOption();
      setIsOpened(false);
      setSelectedIds([]);
      closeModal();
    },
  },
  addUrl: {
    title: "Add Custom URL",
    buttonLabel: "Add URL",
    buttonColor: "primary",
  },
};

const PageTable = ({
  searchValue,
  data,
  pagination,
  setPagination,
  isLoading,
  isFetching,
  setSelectedStatuses,
  setSelectedPageTypes,
  selectedPageTypes,
  selectedCustomizations,
  setSelectedCustomizations,
  selectedStatuses,
  recrawlPage,
  ignoreRecrawlPage,
  recrawlPagesBulk,
  softCrawlPage,
  forceRecrawlPage,
  ignorePagesBulk,
  createPageUrl,
  urlServerError,
  setUrlServerError,
  setSearchValue,
  sorting,
  setSorting,
  pageTypesDetail,
  pageStatusesDetail,
  canGenerate,
  onGenerateClick,
  advancedConditions,
  onAdvancedConditionsChange,
}: PageListProps) => {
  const { id: siteId } = useParams();

  if (!siteId) {
    throw new Error("No site id provided");
  }

  const siteContext = useSiteContext();
  const site = siteContext?.site?.site;
  const sitePermissions = useSitePermissions(siteId);
  const pagesAuthorizations = useMemo(() => data?.authorizations, [data]);
  const pages = data?.pages || [];
  const tableRef = useRef<HTMLDivElement>(null);
  const [tableWidth, setTableWidth] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Ids>([]);
  const [selectedIdsToIgnore, setSelectedIdsToIgnore] = useState<number[]>([]);
  const [selectedIdsToRecrawl, setSelectedIdsToRecrawl] = useState<number[]>(
    [],
  );
  const [accumulatedAuthMap, setAccumulatedAuthMap] = useState(
    new Map<number, string[]>(),
  );
  const [accumulatedStatusMap, setAccumulatedStatusMap] = useState(
    new Map<number, Page["status"]>(),
  );
  const [isOpened, setIsOpened] = useState(false);
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      combobox.focusTarget();
      setIsOpened(false);
    },
    onDropdownOpen: () => {
      setIsOpened(true);
    },
  });
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    type: ModalType;
    crawlType?: "soft" | "force";
  }>({
    isOpen: false,
    type: null,
  });

  const [isCompactView, setIsCompactView] = useState(() => {
    if (typeof window === "undefined") return false;

    const gs = getGlobalSettings();

    return Boolean(gs.pageTableCompactView);
  });

  const toggleCompactView = (value: boolean) => {
    setGlobalSettings({ pageTableCompactView: value });
    setIsCompactView(value);
  };

  useLayoutEffect(() => {
    if (tableRef.current) {
      setTableWidth(tableRef.current.clientWidth);
    }
  }, []);

  // Clear the checkbox selection whenever any filter changes: the visible
  // page set shifts and stale selected ids (now out of view) would silently
  // narrow bulk actions like Generate to rows the user can't see.
  useEffect(() => {
    setSelectedIds([]);
  }, [
    searchValue,
    selectedStatuses,
    selectedPageTypes,
    selectedCustomizations,
    advancedConditions,
  ]);

  const currentSelectableIDs = useMemo(() => {
    return pages
      .filter((_page, index) => {
        const auth = Array.isArray(pagesAuthorizations?.member?.[index])
          ? pagesAuthorizations.member[index]
          : [];

        return auth.includes("recrawl") || auth.includes("toggle_ignore");
      })
      .map((page) => page.id);
  }, [pages, pagesAuthorizations]);

  const allSelected =
    currentSelectableIDs.length > 0 &&
    currentSelectableIDs.every((id) => selectedIds.includes(id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(
        selectedIds.filter((id) => !currentSelectableIDs.includes(id)),
      );
    } else {
      setSelectedIds([
        ...selectedIds,
        ...currentSelectableIDs.filter((id) => !selectedIds.includes(id)),
      ]);
    }
  };

  const toggleRow = (id: number) => {
    setSelectedIds(
      selectedIds.includes(id)
        ? selectedIds.filter((i) => i !== id)
        : [...selectedIds, id],
    );
  };

  useEffect(() => {
    const ignore = selectedIds.filter((id) => {
      const auth = accumulatedAuthMap.get(id) || [];

      return auth.includes("toggle_ignore");
    });

    const recrawl = selectedIds.filter((id) => {
      const auth = accumulatedAuthMap.get(id) || [];

      return auth.includes("recrawl");
    });

    setSelectedIdsToIgnore(ignore);
    setSelectedIdsToRecrawl(recrawl);
  }, [selectedIds, accumulatedAuthMap]);

  useEffect(() => {
    setAccumulatedAuthMap((prevMap) => {
      const newMap = new Map(prevMap);
      let changed = false;

      pages.forEach((page, index) => {
        if (!page || page.id === undefined) return;

        const auth = Array.isArray(pagesAuthorizations?.member?.[index])
          ? [...pagesAuthorizations.member[index]]
          : [];

        newMap.set(page.id, auth);
        changed = true;
      });

      return changed ? newMap : prevMap;
    });
  }, [pages, pagesAuthorizations]);

  useEffect(() => {
    setAccumulatedStatusMap((prevMap) => {
      const newMap = new Map(prevMap);
      let changed = false;

      pages.forEach((page) => {
        if (!page || page.id === undefined) return;

        newMap.set(page.id, page.status);
        changed = true;
      });

      return changed ? newMap : prevMap;
    });
  }, [pages]);

  useEffect(() => {
    const ignore = selectedIds.filter((id) => {
      const auth = accumulatedAuthMap.get(id) || [];
      const canIgnore = auth.includes("toggle_ignore");

      return canIgnore;
    });

    const recrawl = selectedIds.filter((id) => {
      const auth = accumulatedAuthMap.get(id) || [];
      const canRecrawl = auth.includes("recrawl");

      return canRecrawl;
    });

    setSelectedIdsToIgnore(ignore);
    setSelectedIdsToRecrawl(recrawl);
  }, [selectedIds, accumulatedAuthMap]);

  const columnHelper = createColumnHelper<Page>();
  interface ColumnMeta {
    sortField: string;
    headerClassName?: string;
    cellClassName?: string;
    sortable?: boolean;
    /** Custom skeleton for this column's loading cell. Defaults to a single
     * Skeleton bar; override when the live cell renders a taller shape so the
     * skeleton row matches the real row's height. */
    skeletonCell?: React.ReactNode;
  }
  const urlColumnRef = useRef<HTMLTableHeaderCellElement>(null);

  const engineHealthy = isEngineHealthy(site);

  const columns = useMemo(
    () => [
      columnHelper.accessor("url", {
        header: "Page",
        meta: {
          // @ts-expect-error TS2339 non-existing className
          headerClassName: isCompactView && styles.pageCellContent,
          // @ts-expect-error TS2339 non-existing className
          cellClassName: isCompactView && styles.pageCellContent,
          sortable: true,
          sortField: "path",
        },
        cell: (info) => {
          const rowData = info.row.original;
          const url = urlWithHighlight(rowData.url);

          return (
            // @ts-expect-error TS2339 non-existing className
            <div className={styles.pageCellLinkWrapper}>
              <a
                target="_blank"
                rel="noreferrer"
                href={url}
                className={styles.pageCellLink}
              >
                {/*
                 // @ts-expect-error TS2339 non-existing className */}
                <div className={styles.pageLink}>
                  <MiddleTruncateUrl
                    text={rowData?.path_and_query}
                    maxWidth={
                      isCompactView
                        ? urlColumnRef?.current?.offsetWidth || 300
                        : tableWidth
                    }
                  />
                </div>
              </a>
            </div>
          );
        },
      }),
      columnHelper.accessor("customizations", {
        header: "AI Content",
        meta: {
          headerClassName: styles.pageHeaderAiContent,
        },
        cell: (info) => {
          const customizations = info.getValue() || [];

          if (customizations.length === 0) {
            return null;
          }

          const AI_CONTENT_ITEMS: {
            key: "title" | "description" | "schema";
            label: string;
            iconName: "TitleIcon" | "DescriptionIcon" | "JsonLdIcon";
          }[] = [
            { key: "title", label: "Meta Title", iconName: "TitleIcon" },
            {
              key: "description",
              label: "Meta Description",
              iconName: "DescriptionIcon",
            },
            {
              key: "schema",
              label: "JSON-LD Schema",
              iconName: "JsonLdIcon",
            },
          ];

          return (
            <div className={styles.aiContentCell}>
              {AI_CONTENT_ITEMS.filter((item) =>
                customizations.includes(item.key),
              ).map((item) => (
                <span
                  key={item.key}
                  className={styles.aiContentIcon}
                  data-tooltip-id="ai-content-tooltip"
                  data-tooltip-content={item.label}
                >
                  <Icon
                    iconName={item.iconName}
                    width="1.125rem"
                    height="1.125rem"
                  />
                </span>
              ))}
            </div>
          );
        },
      }),
      columnHelper.accessor("status", {
        header: "Status",
        meta: {
          headerClassName: styles.pageHeaderStatus,
          cellClassName: styles.pageCellStatus,
        },
        cell: (info) => {
          const page = info.row.original;
          const crawlerError = page.crawler_error;
          const status = humanizeString(info.getValue());

          return (
            <div className={styles.pageStatusWrapper}>
              <span>{status}</span>
              {!!crawlerError && isCompactView && (
                <>
                  <a data-tooltip-id="crawlerError">
                    <Icon
                      iconName="QuestionMarkInCircleIcon"
                      width="1rem"
                      height="1rem"
                      color="var(--service-red3)"
                      className={styles.statusIcon}
                    />
                  </a>
                  <Tooltip
                    style={{
                      borderRadius: "var(--border-radius)",
                      textTransform: "none",
                    }}
                    id="crawlerError"
                    content={crawlerError}
                    place="top"
                  />
                </>
              )}
            </div>
          );
        },
      }),
      columnHelper.accessor("visibility_report", {
        header: "Visibility / Perf",
        meta: {
          headerClassName: styles.pageHeaderVisPerf,
          cellClassName: styles.pageCellVisPerf,
          skeletonCell: (
            <div className={styles.visPerfSkeleton}>
              <Skeleton height={6} />
              <Skeleton height={6} />
            </div>
          ),
        },
        cell: (info) => (
          <PageVisibilityPerformanceBars
            visibilityReport={info.row.original.visibility_report}
            performanceReports={info.row.original.performance_reports}
            tooltipId="vis-perf-tooltip"
            engineHealthy={engineHealthy}
            isCompactView={isCompactView}
          />
        ),
      }),
      columnHelper.accessor("depth", {
        header: "Depth",
        meta: {
          headerClassName: styles.pageHeaderDepth,
          cellClassName: styles.pageCellContentDepth,
          sortable: true,
        },
        cell: (info) => <span>{info.getValue()}</span>,
      }),
      columnHelper.accessor("lang", {
        header: "Lang",
        meta: {
          headerClassName: styles.pageHeaderLang,
          // @ts-expect-error TS2339 non-existing className
          cellClassName: styles.pageCellContentLang,
          sortable: true,
        },
        cell: (info) => <span>{info.getValue() || "N/A"}</span>,
      }),
      columnHelper.accessor("widget_installed", {
        header: "Widget",
        meta: {
          headerClassName: styles.pageHeaderWidget,
          // @ts-expect-error TS2339 non-existing className
          cellClassName: styles.pageCellContentWidget,
          sortable: true,
        },
        cell: (info) => {
          const status = info.row.original.status;

          const isInstalled =
            info.getValue() ||
            (site &&
              site.prerendering &&
              site.inject_widget &&
              status == "completed");

          // show 'checking' if is not installed and currently in_progress
          const showChecking = isInstalled ? false : status === "in_progress";

          return isInstalled ? (
            <span className={styles.widgetGreen}>Installed</span>
          ) : showChecking ? (
            <span className={styles.widgetBlue}>Checking</span>
          ) : (
            <span className={styles.widgetRed}>Not Installed</span>
          );
        },
      }),
      columnHelper.accessor("crawled_at", {
        header: "Last Crawl",
        meta: {
          headerClassName: styles.pageHeaderLastCrawl,
          // @ts-expect-error TS2339 non-existing className
          cellClassName: styles.pageCellContentLastCrawl,
          sortable: true,
        },
        cell: (info) =>
          info.getValue()
            ? format(
                new Date(info.getValue() as string | number | Date),
                DATE_FORMAT,
              )
            : "N/A",
      }),
      // @ts-expect-error from the response
      columnHelper.accessor("_actions", {
        header: "",
        meta: {
          headerClassName: styles.pageActionHeader,
          cellClassName: styles.pageActionHeader,
        },
        cell: (info) => {
          const row = info.row;
          const rowIndex = row.index;

          const rowAuth: string[] = Array.isArray(
            pagesAuthorizations?.member?.[rowIndex],
          )
            ? pagesAuthorizations?.member?.[rowIndex]
            : [];

          const recrawlEnabled = rowAuth.includes("recrawl");
          const ignoreEnabled = rowAuth.includes("toggle_ignore");

          return (
            <ActionPopover
              id={row.original.id}
              recrawlPage={recrawlPage}
              ignoreRecrawlPage={ignoreRecrawlPage}
              recrawlEnabled={recrawlEnabled}
              ignoreEnabled={ignoreEnabled}
              setSelectedIds={setSelectedIds}
              pageStatus={row.original.status}
            />
          );
        },
      }),
    ],
    [columnHelper, tableWidth, isCompactView, engineHealthy],
  );
  const table = useReactTable({
    data: pages,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      url: "",
    },
    mode: "onChange",
  });

  const onSubmit = (data: { url: string }) => {
    createPageUrl(data.url);
    setUrlServerError("");
    setModalState({
      isOpen: false,
      type: null,
    });
    reset();
  };
  const handlePageChange = (page: number) =>
    setPagination({ ...pagination, currentPage: page });
  const handleItemsPerPageChange = (items: number) =>
    setPagination({ ...pagination, itemsPerPage: items, currentPage: 1 });
  const isNoPages = (!pages || pages.length === 0) && !isLoading;

  const canSoftCrawl = sitePermissions.canSoftCrawl;
  const canForceRecrawl = sitePermissions.canForceRecrawl;
  const canAbortCrawl = sitePermissions.canAbortCrawl;

  const canAddUrl =
    pagesAuthorizations?.collection?.includes("create") ?? false;

  const hasMixedIgnoreStatuses = useMemo(() => {
    // We only inspect rows that are actually eligible for toggle_ignore.
    // selectedIds may include rows without that authorization, but they are
    // filtered out earlier into selectedIdsToIgnore.
    const selectedStatuses = selectedIdsToIgnore
      .map((id) => accumulatedStatusMap.get(id))
      .filter((status): status is Page["status"] => Boolean(status));

    if (selectedStatuses.length === 0) return false;

    // "Pending group" here means statuses that would be moved back to pending
    // by the toggle action.
    const hasPendingGroup = selectedStatuses.some((status) =>
      IGNORE_COMPATIBLE_PENDING_STATUSES.has(status),
    );
    // "Ignored group" means statuses that would be moved to ignored
    // by the same toggle action.
    const hasIgnoredGroup = selectedStatuses.some((status) =>
      IGNORE_COMPATIBLE_IGNORED_STATUSES.has(status),
    );

    // Mixed selection means one click would attempt both directions at once:
    // ignored/noindex -> pending and pending/failed/completed -> ignored.
    // We intentionally block that in the UI instead of showing a confusing
    // partial/dual-action confirmation dialog.
    return hasPendingGroup && hasIgnoredGroup;
  }, [accumulatedStatusMap, selectedIdsToIgnore]);

  const bulkIgnoreAction = useMemo<BulkIgnoreAction>(() => {
    const selectedStatuses = selectedIdsToIgnore
      .map((id) => accumulatedStatusMap.get(id))
      .filter((status): status is Page["status"] => Boolean(status));

    return getBulkIgnoreActionFromStatuses(selectedStatuses);
  }, [accumulatedStatusMap, selectedIdsToIgnore]);

  const openCrawlModal = (type: "soft" | "force") => {
    setModalState({
      isOpen: true,
      type: "crawl",
      crawlType: type,
    });
    combobox.resetSelectedOption();
    setIsOpened(false);
  };

  const openBulkIgnoreModal = () => {
    setModalState({
      isOpen: true,
      type: "bulkIgnore",
    });
    combobox.resetSelectedOption();
    setIsOpened(false);
  };

  const openAddUrlModal = () => {
    setModalState({
      isOpen: true,
      type: "addUrl",
    });
  };

  const closeModal = () => {
    if (modalState.type === "addUrl") {
      setUrlServerError("");
      reset();
    }
    setModalState({
      isOpen: false,
      type: null,
    });
  };

  const handleSortColumn = (columnId: string) => {
    const currentDirection = getSortStateForColumn(columnId, sorting);

    const newSorting = sorting.filter(
      (entry) => !entry.startsWith(`${columnId} `),
    );

    if (currentDirection === "ASC") {
      newSorting.push(`${columnId} DESC`);
      //eslint-disable-next-line
    } else if (currentDirection === "DESC") {
    } else {
      newSorting.push(`${columnId} ASC`);
    }

    setSorting(newSorting);
  };

  const handleModalAction = () => {
    switch (modalState.type) {
      case "crawl":
        MODAL_CONFIGS.crawl.action(
          closeModal,
          softCrawlPage,
          forceRecrawlPage,
          modalState.crawlType,
        );
        break;
      case "bulkIgnore":
        MODAL_CONFIGS.bulkIgnore.action(
          closeModal,
          ignorePagesBulk,
          selectedIdsToIgnore,
          combobox,
          setIsOpened,
          setSelectedIds,
        );
        break;
      case "addUrl":
        return handleSubmit(onSubmit)();
      default:
        break;
    }
  };

  const getModalConfig = () => {
    if (!modalState.type) return null;

    if (modalState.type === "crawl") {
      return {
        title: MODAL_CONFIGS.crawl.title(
          modalState.crawlType as "soft" | "force",
        ),
        buttonLabel: MODAL_CONFIGS.crawl.buttonLabel,
        buttonColor: MODAL_CONFIGS.crawl.buttonColor,
      };
    }

    if (modalState.type === "bulkIgnore") {
      return {
        title: MODAL_CONFIGS.bulkIgnore.title(bulkIgnoreAction),
        buttonLabel: MODAL_CONFIGS.bulkIgnore.buttonLabel(bulkIgnoreAction),
        buttonColor: MODAL_CONFIGS.bulkIgnore.buttonColor,
      };
    }

    return {
      title: MODAL_CONFIGS.addUrl.title,
      buttonLabel: MODAL_CONFIGS.addUrl.buttonLabel,
      buttonColor: MODAL_CONFIGS.addUrl.buttonColor,
    };
  };

  const renderModalContent = () => {
    switch (modalState.type) {
      case "crawl":
        return (
          <CrawlModalContent
            crawlType={modalState.crawlType as "soft" | "force"}
          />
        );
      case "bulkIgnore":
        return (
          <BulkIgnoreModalContent
            count={selectedIdsToIgnore.length}
            action={bulkIgnoreAction}
          />
        );
      case "addUrl":
        return (
          <AddUrlModalContent
            register={register}
            errors={errors}
            urlServerError={urlServerError}
          />
        );
      default:
        return null;
    }
  };

  const modalConfig = getModalConfig();

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const pageTypeParam = searchParams.get("pageType");

    if (pageTypeParam) {
      setSelectedPageTypes([pageTypeParam]);
      searchParams.delete("pageType");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, setSelectedPageTypes]);

  return (
    <>
      <PageFilters
        setPagination={setPagination}
        pagination={pagination}
        pageTypesDetail={pageTypesDetail}
        pageStatusesDetail={pageStatusesDetail}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        selectedStatuses={selectedStatuses}
        selectedPageTypes={selectedPageTypes}
        setSelectedPageTypes={setSelectedPageTypes}
        selectedCustomizations={selectedCustomizations}
        setSelectedCustomizations={setSelectedCustomizations}
        setSelectedStatuses={setSelectedStatuses}
        selectedIds={selectedIds}
        selectedIdsToRecrawl={selectedIdsToRecrawl}
        selectedIdsToIgnore={selectedIdsToIgnore}
        canSoftCrawl={canSoftCrawl}
        canForceRecrawl={canForceRecrawl}
        canAbortCrawl={canAbortCrawl}
        recrawlPagesBulk={recrawlPagesBulk}
        openBulkIgnoreModal={openBulkIgnoreModal}
        openCrawlModal={openCrawlModal}
        setSelectedIds={setSelectedIds}
        isLoading={isLoading}
        hasMixedIgnoreStatuses={hasMixedIgnoreStatuses}
        combobox={combobox}
        isOpened={isOpened}
        setIsOpened={setIsOpened}
        openAddUrlModal={openAddUrlModal}
        isCompactView={isCompactView}
        setIsCompactView={toggleCompactView}
        canAddUrl={canAddUrl}
        canGenerate={canGenerate}
        onGenerateClick={onGenerateClick}
        advancedConditions={advancedConditions}
        onAdvancedConditionsChange={onAdvancedConditionsChange}
      />

      {isNoPages ? (
        <NoData title="No Pages" />
      ) : (
        <>
          <div className={styles.tablePage} ref={tableRef}>
            <table className={styles.tablePageTable} role="grid">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      onClick={(e) => e.stopPropagation()}
                      aria-label="Select all rows"
                      disabled={currentSelectableIDs.length === 0}
                    />
                  </th>
                  {table.getHeaderGroups()[0].headers.map((header) => {
                    const meta = header.column.columnDef.meta as ColumnMeta;
                    const isSortable = meta?.sortable;
                    const sortField = meta?.sortField || header.id;
                    const sortDirection = isSortable
                      ? getSortStateForColumn(sortField, sorting)
                      : null;
                    const isUrlColumn = header.id === "url";

                    return (
                      <th
                        ref={isUrlColumn ? urlColumnRef : null}
                        key={header.id}
                        className={meta?.headerClassName || ""}
                        onClick={
                          isSortable
                            ? () => handleSortColumn(sortField)
                            : undefined
                        }
                      >
                        <div
                          className={isSortable ? styles.tableSortHeader : ""}
                        >
                          <div>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                          </div>
                          {isSortable && (
                            <div className={styles.sortIcon}>
                              {sortDirection === null && (
                                <Icon iconName="SortingIcon" />
                              )}
                              {sortDirection === "ASC" && (
                                <Icon iconName="SortingIconUp" />
                              )}
                              {sortDirection === "DESC" && (
                                <Icon iconName="SortingIconDown" />
                              )}
                              {sortDirection && (
                                <div className={styles.sortIconOrder}>
                                  {sorting.findIndex((entry) =>
                                    entry.startsWith(`${sortField} `),
                                  ) + 1}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {isLoading
                  ? Array.from({ length: 10 }).map((_, rowIndex) => (
                      <React.Fragment key={`skeleton-fragment-${rowIndex}`}>
                        {!isCompactView && (
                          <tr key={`skeleton-main-${rowIndex}`}>
                            <td>
                              <Skeleton width={20} height={20} />
                            </td>
                            <td colSpan={columns.length}>
                              <Skeleton height={20} />
                            </td>
                          </tr>
                        )}

                        <tr key={`skeleton-sub-${rowIndex}`}>
                          <td />
                          {columns.slice(1).map((column, colIndex) => {
                            const meta = column.meta as ColumnMeta | undefined;

                            return (
                              <td key={`skeleton-sub-cell-${colIndex}`}>
                                {meta?.skeletonCell ?? <Skeleton height={20} />}
                              </td>
                            );
                          })}
                        </tr>
                      </React.Fragment>
                    ))
                  : table.getRowModel().rows.map((row, rowIndex) => {
                      const rowAuth: string[] = Array.isArray(
                        pagesAuthorizations?.member?.[rowIndex],
                      )
                        ? pagesAuthorizations?.member?.[rowIndex]
                        : [];
                      const recrawlEnabled = rowAuth.includes("recrawl");
                      const ignoreEnabled = rowAuth.includes("toggle_ignore");
                      const isSelectable = recrawlEnabled || ignoreEnabled;

                      return isCompactView ? (
                        <tr key={row.id}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(row.original.id)}
                              onChange={() => toggleRow(row.original.id)}
                              aria-label={`Select row ${row.id}`}
                              disabled={!isSelectable}
                            />
                          </td>
                          {row.getVisibleCells().map((cell) => {
                            const meta = cell.column.columnDef
                              .meta as ColumnMeta;

                            return (
                              <td
                                key={cell.id}
                                className={meta?.cellClassName || ""}
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext(),
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ) : (
                        <React.Fragment key={row.id}>
                          <tr
                            key={row.id + "-main"}
                            className={styles.expandedTableTr}
                          >
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedIds.includes(row.original.id)}
                                onChange={() => toggleRow(row.original.id)}
                                aria-label={`Select row ${row.id}`}
                                disabled={!isSelectable}
                              />
                            </td>
                            <td
                              colSpan={columns.length}
                              className={
                                (
                                  row.getVisibleCells()[0].column.columnDef
                                    .meta as ColumnMeta
                                )?.cellClassName || ""
                              }
                            >
                              {flexRender(
                                row.getVisibleCells()[0].column.columnDef.cell,
                                row.getVisibleCells()[0].getContext(),
                              )}
                            </td>
                          </tr>
                          <tr key={row.id + "-sub"}>
                            <td className={styles.checkboxCellEmpty}></td>
                            <td>
                              {!!row.original.crawler_error && (
                                <span className={styles.pageCrawlerError}>
                                  {row.original.crawler_error}
                                </span>
                              )}
                            </td>
                            {row
                              .getVisibleCells()
                              .slice(1)
                              .map((cell) => {
                                const meta = cell.column.columnDef
                                  .meta as ColumnMeta;

                                return (
                                  <td
                                    key={cell.id}
                                    className={meta?.cellClassName || ""}
                                  >
                                    {flexRender(
                                      cell.column.columnDef.cell,
                                      cell.getContext(),
                                    )}
                                  </td>
                                );
                              })}
                          </tr>
                        </React.Fragment>
                      );
                    })}
              </tbody>
            </table>
          </div>
          <Pagination
            {...pagination}
            isLoading={isFetching ?? isLoading}
            totalItems={pagination.totalItems}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </>
      )}

      {modalConfig && (
        <Modal
          formId={modalState.type === "addUrl" ? "addUrlModal" : undefined}
          isOpen={modalState.isOpen}
          closeModal={closeModal}
          modalLabel={
            // @ts-expect-error TS2339 non-existing className
            <h3 className={styles.modalTitle}>{modalConfig.title}</h3>
          }
          buttonLabel={modalConfig.buttonLabel}
          action={handleModalAction}
          actionButtonColor={modalConfig.buttonColor}
        >
          {renderModalContent()}
        </Modal>
      )}
      <Tooltip
        id="ai-content-tooltip"
        style={{ borderRadius: "var(--border-radius)", fontSize: "0.75rem" }}
      />
      <Tooltip
        id="vis-perf-tooltip"
        anchorSelect="[data-tooltip-id='vis-perf-tooltip']"
        style={{ borderRadius: "var(--border-radius)", fontSize: "0.75rem" }}
      />
    </>
  );
};

export default PageTable;
